import { useEffect, useState } from "react";
import {
  message,
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Radio,
  Row,
  Select,
  Alert,
} from "antd";
import { nonNegativeRules } from "../numericValidation";
import { addGoods, editGoods, listGoodsProvinces } from "@/api/shopGoods";
import { GoodsDescriptionEditor, uploadGoodsDescriptionImage } from "./GoodsDescriptionEditor";
import { prepareGoodsDescription } from "@/utils/goodsDescription";
import type { EntityId, GoodsDetail, GoodsRemoteArea } from "@/types/shopGoods";
import "react-quill/dist/quill.snow.css";
import { ProductImageInput } from "./ProductImageInput";
import { ProductCategorySelect } from "./ProductCategorySelect";
import { useSpecifications } from "./useSpecifications";
import { remapSkuSpecifications } from "./specifications";
import { SkuSpecificationSelect } from "./SkuSpecificationSelect";
import styles from "./ProductDialog.module.css";
import { getErrorMessage } from "./goodsLabels";
import { stockTypes, goodsTypes } from "./goodsLabels";
import {
  emptySku,
  goodsFormValues,
  goodsPayload,
  validateGoodsShelf,
  type GoodsFormValues,
} from "./goodsForm";

interface GoodsEditorProps {
  detail?: GoodsDetail;
  onSaved: () => void;
  onCancel: () => void;
  onBusyChange: (busy: boolean) => void;
}
const requiredText = [
  { required: true, whitespace: true, message: "请填写此项" },
];
const required = [{ required: true, message: "请填写此项" }];
const yesNo = [
  { label: "否", value: 0 },
  { label: "是", value: 1 },
];
const skuNumbers = [
  { name: "price", label: "销售价格（元）", required: true, precision: 2 },
  { name: "marketPrice", label: "市场原价（元）", precision: 2 },
  { name: "costPrice", label: "结算价（元）", precision: 2, required: true },
  { name: "stock", label: "库存", required: true, precision: 0 },
  { name: "stockWarn", label: "库存预警", precision: 0 },
  { name: "weight", label: "重量（kg）", precision: 3 },
  { name: "volume", label: "体积（m³）", precision: 3 },
  { name: "sort", label: "排序", precision: 0 },
];

export function GoodsEditor({
  detail,
  onSaved,
  onCancel,
  onBusyChange,
}: GoodsEditorProps): React.ReactElement {
  const [form] = Form.useForm<GoodsFormValues>();
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [provinces, setProvinces] = useState<GoodsRemoteArea[]>([]);
  const [provincesLoading, setProvincesLoading] = useState(true);
  useEffect(() => {
    const controller = new AbortController();
    setProvincesLoading(true);
    void listGoodsProvinces(controller.signal).then(({ data }) => {
      if (controller.signal.aborted) return;
      const rows = (data ?? []).filter((area) => area.levelType === 1);
      setProvinces(rows);
      if (!detail && form.getFieldValue("remoteAreaIds") === undefined) {
        form.setFieldValue("remoteAreaIds", rows
          .filter((area) => /^(西藏|新疆|青海|内蒙古)/.test(area.name))
          .map((area) => String(area.id)));
      }
    }).catch(() => {
      // 请求层统一提示错误；不将加载失败当作空地区配置。
    }).finally(() => {
      if (!controller.signal.aborted) setProvincesLoading(false);
    });
    return (): void => controller.abort();
  }, [detail, form]);
  const provinceOptions = provinces.map((area) => ({ value: String(area.id), label: area.name }));
  for (const area of detail?.remoteAreaList ?? []) {
    if (!provinceOptions.some((option) => option.value === String(area.id)))
      provinceOptions.push({ value: String(area.id), label: area.name });
  }
  const handleUploadBusy = (busy: boolean): void => {
    setUploading(busy);
    onBusyChange(busy);
  };
  const multi = Form.useWatch<number>("isMultiSpecification", form) === 1;
  const selectedSpecs = Form.useWatch<GoodsFormValues["specRelList"]>("specRelList", form) ?? [];
  const specificationData = useSpecifications(multi);
  const specOptions = specificationData.specs.map((spec) => ({
    value: String(spec.id), label: spec.specName ?? String(spec.id), disabled: spec.status !== 1,
  }));
  for (const spec of detail?.specRelList ?? []) {
    if (!specOptions.some((option) => option.value === String(spec.specId)))
      specOptions.push({ value: String(spec.specId), label: spec.specName || `规格项 ${spec.specId}`, disabled: true });
  }
  const handleSpecChange = (index: number, value: string): void => {
    const current = form.getFieldsValue();
    const previous = selectedSpecs;
    const next = previous.map((spec, position) => position === index
      ? { ...spec, specId: value, specName: specOptions.find((option) => option.value === value)?.label }
      : spec);
    form.setFieldsValue({ specRelList: next, skuList: remapSkuSpecifications(current.skuList, previous, next) });
  };
  const goodsType = Form.useWatch<number>("goodsType", form);
  const stockType = Form.useWatch<number>("stockType", form);
  const logisticsType = Form.useWatch<number>("logisticsType", form);
  const timing = Form.useWatch<number>("isTimingShelf", form) === 1;
  const handleSave = async (values: GoodsFormValues): Promise<void> => {
    if (uploading || submitting || detail?.isShelf === 1) return;
    if (values.logisticsType === 2 && (provincesLoading || provinces.length === 0)) {
      void message.error(provincesLoading ? "省份正在加载，请稍后保存" : "省份加载失败，请重新打开弹窗重试");
      return;
    }
    setSubmitting(true);
    onBusyChange(true);
    try {
      validateGoodsShelf(values, detail?.auditStatus);
      const payload = goodsPayload(
        detail ? values : { ...values, goodsType: 1 },
        detail?.skuList
      );
      payload.goodsDesc = await prepareGoodsDescription(payload.goodsDesc, uploadGoodsDescriptionImage,
        (html) => form.setFieldValue("goodsDesc", html));
      if (detail)
        await editGoods({
          ...payload,
          id: detail.id,
        });
      else await addGoods(payload);
      void message.success(values.goodsType === 1
        ? "自营商品已保存，无需审核，可直接上架"
        : detail ? "商品已保存，已重置为待审核并下架" : "商品已创建，审核通过后可上架");
      onSaved();
    } catch (error: unknown) {
      void message.error(getErrorMessage(error));
    } finally {
      setSubmitting(false);
      onBusyChange(false);
    }
  };
  if (detail?.isShelf === 1) {
    return <Alert type="warning" showIcon message="请先下架商品后再编辑" action={<Button onClick={onCancel}>关闭</Button>} />;
  }
  return (
    <Form<GoodsFormValues>
      name="mall-product-editor"
      form={form}
      className={styles.editor}
      scrollToFirstError={{ block: "center" }}
      layout="vertical"
      initialValues={goodsFormValues(detail)}
      onFinish={(values) => void handleSave(values)}
      disabled={submitting || uploading}
    >
      {/* 暂不开放供应商配置，保留字段注册，避免编辑保存时丢失已有供货关联。 */}
      <Form.Item name="supplyType" hidden>
        <Input type="hidden" />
      </Form.Item>
      <Form.Item name="supplierId" hidden>
        <Input type="hidden" />
      </Form.Item>
      <div className={styles.scrollBody}>
        <section className={styles.section}>
          <div className={styles.sectionHeading}>
            <h3>基本信息</h3>
          </div>
          <Row gutter={24}>
            <Col xs={24} md={12}>
              <Form.Item name="goodsName" label="商品名称" rules={requiredText}>
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="goodsSubtitle" label="商品副标题">
                <Input />
              </Form.Item>
            </Col>
            {goodsType === 1 && (
              <Col xs={24} md={12}>
                <Form.Item
                  name="selfSupplier"
                  label="自营供应商"
                  rules={[{ required: true, whitespace: true, message: "请填写自营供应商" }]}
                >
                  <Input placeholder="请输入自营供应商" />
                </Form.Item>
              </Col>
            )}
            <Col span={24}>
              <Form.Item name="categoryIds" label="商品分类">
                <ProductCategorySelect
                  existingCategories={detail?.categoryRelList}
                  disabled={submitting || uploading}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="goodsType" label="商品类型" rules={required}>
                <Select options={goodsTypes} disabled={!detail || submitting || uploading} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="stockType" label="库存类型" rules={required}>
                <Select options={stockTypes} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="sort" label="排序" rules={nonNegativeRules("商品排序", true)}>
                <InputNumber min={0} precision={0} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} hidden={stockType !== 2}>
              <Form.Item
                name="preSaleTime"
                label="预售结束/可开始发货时间"
                hidden={stockType !== 2}
                rules={stockType === 2 ? [{ required: true, message: "请选择预售结束/可开始发货时间" }] : []}
              >
                <DatePicker showTime format="YYYY-MM-DD HH:mm:ss" placeholder="请选择预售结束/可开始发货时间" />
              </Form.Item>
            </Col>
            <Col span={24} hidden={stockType !== 2}>
              <Form.Item
                name="preSaleNote"
                label="预售补充说明"
                hidden={stockType !== 2}
              >
                <Input.TextArea rows={2} />
              </Form.Item>
            </Col>
          </Row>
        </section>
        <section className={styles.section}>
          <div className={styles.sectionHeading}>
            <h3>商品图片与介绍</h3>
          </div>
          <Row gutter={24}>
            <Col span={24}>
              <Form.Item name="mainImage" label="商品主图" rules={requiredText}>
                <ProductImageInput
                  disabled={submitting || uploading}
                  onBusyChange={handleUploadBusy}
                />
              </Form.Item>
            </Col>
          </Row>{" "}
          <Card title="轮播图" size="small" className={styles.innerCard}>
            <Form.List name="slideList">
              {(fields, { add, remove }) => (
                <>
                  {fields.map((field) => (
                    <Row
                      gutter={16}
                      align="top"
                      className={styles.repeatRow}
                      key={field.key}
                    >
                      <Col flex="1">
                        <Form.Item
                          name={[field.name, "slideImage"]}
                          label="轮播图片"
                          rules={requiredText}
                        >
                          <ProductImageInput
                            disabled={submitting || uploading}
                            onBusyChange={handleUploadBusy}
                          />
                        </Form.Item>
                      </Col>
                      <Col flex="120px">
                        <Form.Item name={[field.name, "sort"]} label="排序" rules={nonNegativeRules("排序", true)}>
                          <InputNumber min={0} precision={0} />
                        </Form.Item>
                      </Col>
                      <Col>
                        <Form.Item label=" " colon={false}>
                          <Button danger onClick={() => remove(field.name)}>
                            移除
                          </Button>
                        </Form.Item>
                      </Col>
                    </Row>
                  ))}
                  <Button
                    onClick={() => add({ slideImage: "", sort: fields.length })}
                  >
                    添加轮播图
                  </Button>
                </>
              )}
            </Form.List>
          </Card>
          <Form.Item name="goodsDesc" label="商品详情">
            <GoodsDescriptionEditor disabled={submitting || uploading} onBusyChange={handleUploadBusy} />
          </Form.Item>
        </section>
        <section className={styles.section}>
          <div className={styles.sectionHeading}>
            <h3>规格与库存</h3>
          </div>
          <Form.Item
            name="isMultiSpecification"
            label="规格模式"
            rules={required}
          >
            <Radio.Group
              options={[
                { label: "单规格", value: 0 },
                { label: "多规格", value: 1 },
              ]}
            />
          </Form.Item>
          <div hidden={!multi}>
            <Card title="规格项" size="small" className={styles.innerCard}>
              <Form.List name="specRelList">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map((field) => (
                      <Row
                        gutter={16}
                        align="bottom"
                        className={styles.repeatRow}
                        key={field.key}
                      >
                        <Col>
                          <Form.Item
                            name={[field.name, "specId"]}
                            label="规格项"
                            rules={multi ? required : []}
                            getValueProps={(value: EntityId) => ({ value: value == null || value === "" ? undefined : String(value) })}
                          >
                            <Select
                              style={{ minWidth: 200 }} showSearch optionFilterProp="label"
                              getPopupContainer={(trigger) => trigger.parentElement ?? trigger}
                              options={specOptions.map((option) => ({ ...option, disabled: option.disabled || selectedSpecs.some((spec, index) => index !== field.name && String(spec.specId) === option.value) }))}
                              loading={specificationData.loading}
                              disabled={submitting || uploading || specificationData.loading || specificationData.failed}
                              onChange={(value: string) => handleSpecChange(field.name, value)}
                            />
                          </Form.Item>
                        </Col>
                        <Col>
                          <Form.Item
                            name={[field.name, "specName"]}
                            hidden
                          >
                            <Input readOnly />
                          </Form.Item>
                        </Col>
                        <Col>
                          <Form.Item name={[field.name, "sort"]} label="排序" rules={multi ? nonNegativeRules("排序", true) : []}>
                            <InputNumber min={0} precision={0} />
                          </Form.Item>
                        </Col>
                        <Col>
                          <Button danger onClick={() => {
                            const current = form.getFieldsValue();
                            const next = current.specRelList.filter((_, index) => index !== field.name);
                            remove(field.name);
                            form.setFieldValue("skuList", remapSkuSpecifications(current.skuList, current.specRelList, next));
                          }}>
                            移除
                          </Button>
                        </Col>
                      </Row>
                    ))}
                    <Button
                      onClick={() => {
                        const current = form.getFieldsValue();
                        const next = [...current.specRelList, { specId: "", sort: fields.length }];
                        add({ specId: "", sort: fields.length });
                        form.setFieldValue("skuList", remapSkuSpecifications(current.skuList, current.specRelList, next));
                      }}
                    >
                      添加规格项
                    </Button>
                  </>
                )}
              </Form.List>
            </Card>
          </div>
          <Form.List name="skuList">
            {(fields, { add, remove }) => (
              <>
                {fields.map((field) => (
                  <Card
                    key={field.key}
                    title={multi ? `SKU ${field.name + 1}` : "单规格库存与价格"}
                    size="small"
                    style={{
                      marginBottom: 16,
                      display: !multi && field.name > 0 ? "none" : undefined,
                    }}
                    extra={
                      multi ? (
                        <Button danger onClick={() => remove(field.name)}>
                          移除 SKU
                        </Button>
                      ) : null
                    }
                  >
                    <Form.Item name={[field.name, "id"]} hidden>
                      <Input />
                    </Form.Item>
                    <Row gutter={24}>
                      <Col xs={24} md={12}>
                        <Form.Item
                          name={[field.name, "skuName"]}
                          label="SKU 名称"
                          rules={multi || field.name === 0 ? requiredText : []}
                        >
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12} hidden={!multi}>
                        <Form.Item
                          name={[field.name, "skuSpecValueIds"]}
                          label="规格组合"
                          hidden={!multi}
                          rules={multi ? requiredText : []}
                        >
                          <SkuSpecificationSelect
                            specs={selectedSpecs.map((spec) => ({ ...spec, specName: specificationData.specs.find((item) => String(item.id) === String(spec.specId))?.specName ?? spec.specName }))} options={specificationData.values}
                            disabled={submitting || uploading || specificationData.loading || specificationData.failed}
                            originalIds={detail?.skuList.find((sku) => sku.id === form.getFieldValue(["skuList", field.name, "id"]))?.skuSpecValueIds}
                            originalNames={detail?.skuList.find((sku) => sku.id === form.getFieldValue(["skuList", field.name, "id"]))?.skuSpecValueNames}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item
                          name={[field.name, "skuCode"]}
                          label="SKU 编码"
                        >
                          <Input readOnly placeholder="保存后自动生成" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item
                          name={[field.name, "barCode"]}
                          label="商品条形码"
                        >
                          <Input />
                        </Form.Item>
                      </Col>
                      {skuNumbers.map((item) => (
                        <Col xs={12} md={6} key={item.name}>
                          <Form.Item
                            name={[field.name, item.name]}
                            label={item.label}
                            rules={
                              multi || field.name === 0
                                ? item.name === "price"
                                  ? [{ required: true, type: "number", min: 0.01, message: "销售价格必须大于0" }]
                                  : nonNegativeRules(item.label, item.precision === 0, item.required === true)
                                : []
                            }
                          >
                            <InputNumber
                              min={item.name === "price" ? 0.01 : 0}
                              precision={item.precision}
                              style={{ width: "100%" }}
                            />
                          </Form.Item>
                        </Col>
                      ))}
                      <Col span={24}>
                        <Form.Item
                          name={[field.name, "skuImage"]}
                          label="SKU 图片"
                          rules={multi || field.name === 0 ? [{ required: true, whitespace: true, message: "请上传 SKU 图片" }] : []}
                        >
                          <ProductImageInput
                            disabled={submitting || uploading}
                            onBusyChange={handleUploadBusy}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Card>
                ))}
                {(multi || fields.length === 0) && (
                  <Button
                    onClick={() =>
                      add({
                        ...emptySku(),
                        skuName: multi ? "" : "默认规格",
                        sort: fields.length,
                      })
                    }
                  >
                    添加 SKU
                  </Button>
                )}
              </>
            )}
          </Form.List>
        </section>
        <section className={styles.section}>
          <div className={styles.sectionHeading}>
            <h3>配送与上架</h3>
          </div>
          <Row gutter={24}>
            <Col xs={24} md={12}>
              <Form.Item name="logisticsType" label="物流类型" rules={required}>
                <Select
                  options={[
                    { label: "不包邮", value: 0 },
                    { label: "包邮", value: 1 },
                    { label: "偏远地区不包邮", value: 2 },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} hidden={logisticsType !== 0 && logisticsType !== 2}>
              <Form.Item
                name="postage"
                label={logisticsType === 2 ? "偏远地区邮费（元）" : "邮费（元）"}
                hidden={logisticsType !== 0 && logisticsType !== 2}
                rules={logisticsType === 0 || logisticsType === 2 ? nonNegativeRules("邮费", false, true) : []}
              >
                <InputNumber min={0} precision={2} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} hidden={logisticsType !== 2}>
              <Form.Item name="remoteAreaIds" label="偏远地区选择" hidden={logisticsType !== 2}>
                <Select mode="multiple" allowClear showSearch optionFilterProp="label"
                  placeholder="请选择省份" options={provinceOptions} loading={provincesLoading}
                  disabled={submitting || uploading || provincesLoading || provinces.length === 0} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} style={{ display: "none" }}>
              <Form.Item name="isSevenReturn" label="七天无理由">
                <Radio.Group options={yesNo} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} style={{ display: "none" }}>
              <Form.Item name="aftersaleType" label="售后保障">
                <Select
                  options={[
                    { label: "不支持七天无理由", value: 0 },
                    { label: "支持七天无理由", value: 1 },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="isShelf"
                label="上架设置"
                rules={required}
              >
                <Radio.Group
                  options={[
                    { label: "下架", value: 0 },
                    { label: "上架", value: 1, disabled: goodsType !== 1 },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} style={{ display: "none" }}>
              <Form.Item name="isTimingShelf" label="定时上架" extra={goodsType === 1 ? "自营商品无需审核，可设置上架计划。" : "可提前设置上架计划，实际执行上架须审核通过。"}>
                <Radio.Group aria-label="定时上架" options={yesNo} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} hidden={!timing}>
              <Form.Item
                name="timingShelfTime"
                label="定时上架时间"
                hidden={!timing}
                rules={timing ? required : []}
              >
                <DatePicker showTime />
              </Form.Item>
            </Col>
          </Row>
        </section>
      </div>
      <div className={styles.actions}>
        <Button onClick={onCancel}>取消</Button>
        <Button type="primary" htmlType="submit" loading={submitting}>
          保存商品
        </Button>
      </div>
    </Form>
  );
}
